import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { loadAdminUsers } from '../../../action/user/load-admin-users';
import { adminUsersSelector } from '../../../reducers/user-selectors';

import { UiButton } from '../../../ui/ui-button';

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadAdminUsers: () => dispatch(loadAdminUsers()),
  },
});

const mapStateToProps = (state: any) => ({
  adminUsers: adminUsersSelector(state),
});

type InputProps = {
  accountExecutiveUsername?: string | null;
  handleChange: (value: string) => void;
  handleSave?: () => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps & StateProps;

type AccountExecutiveOption = SelectOption<string>;

export const AddAccountExecutive = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { adminUsers, accountExecutiveUsername, handleChange, handleSave, actions } = props;
  const noAccountExecutiveOption: AccountExecutiveOption = {
    value: '',
    label: 'No Account Executive',
  };

  const [selectedOption, setSelectedOption] =
    useState<AccountExecutiveOption>(noAccountExecutiveOption);
  const [isChangeUnsaved, setIsChangeUnsaved] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const onChange = (e: React.ChangeEvent<{}>, accountExecutiveOption: AccountExecutiveOption) => {
    setIsChangeUnsaved(true);
    setSelectedOption(accountExecutiveOption);
    handleChange(accountExecutiveOption.value);
  };

  const onSave = () => {
    if (!handleSave) return;

    handleSave();
    setIsChangeUnsaved(false);
  };

  const onFocus = () => setOpen(true);
  const onBlur = () => setOpen(false);

  useEffect(() => {
    actions.loadAdminUsers();
  });

  const accountExecutiveOptions: Array<AccountExecutiveOption> = adminUsers.map((user) => ({
    value: user.username,
    label: `${user.firstName} ${user.lastName}`,
  }));
  accountExecutiveOptions.push(noAccountExecutiveOption);
  const initialOption =
    accountExecutiveOptions.find((option) => option.value === accountExecutiveUsername) ??
    noAccountExecutiveOption;

  // conditionally set initial option, we have to wait until the account loads in the parent to be sure we have an accountExecutive present
  if (
    accountExecutiveUsername &&
    selectedOption.value === noAccountExecutiveOption.value &&
    initialOption.value !== noAccountExecutiveOption.value
  ) {
    setSelectedOption(initialOption);
  }

  return (
    <Paper className="u-padding u-margin-top" variant="outlined">
      <div className="typo-label">Account Owner</div>
      <Autocomplete
        disableClearable
        open={open}
        onBlur={onBlur}
        onFocus={onFocus}
        blurOnSelect
        size="small"
        options={accountExecutiveOptions}
        getOptionLabel={(option) => option.label}
        onChange={onChange}
        value={accountExecutiveOptions.find((option) => option.value === selectedOption.value)}
        debug
        renderInput={(params) => <TextField {...params} fullWidth required />}
      />
      {handleSave && (
        <div className="u-padding-top u-display-flex u-justifyContent-flexEnd">
          <UiButton onClick={onSave} disabled={!isChangeUnsaved}>
            Save
          </UiButton>
        </div>
      )}
    </Paper>
  );
});
